import { useEffect } from 'react';

interface KeyboardShortcutsConfig {
  onPlayPause: () => void;
  onNextTrack: () => void;
  onPreviousTrack: () => void;
  enabled?: boolean;
}

/**
 * Custom hook for keyboard shortcuts on the Dashboard page
 *
 * Shortcuts:
 * - Space: Play/Pause timer and audio
 * - ArrowRight: Skip to next track
 * - ArrowLeft: Go to previous track
 * - ArrowUp: Volume up
 * - ArrowDown: Volume down
 * - F: Add current track to favorites
 * - N: Add new task
 * - ?: Toggle keyboard shortcuts help
 *
 * @param config - Configuration object with handlers and enabled state
 */
export function useKeyboardShortcuts(config: KeyboardShortcutsConfig) {
  const { onPlayPause, onNextTrack, onPreviousTrack, enabled = true } = config;

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field or textarea
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (isTyping) return;

      // Handle keyboard shortcuts
      switch (e.code) {
        case 'Space':
          e.preventDefault(); // Prevent page scroll
          e.stopPropagation(); // Prevent triggering focused elements
          onPlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          e.stopPropagation();
          onNextTrack();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          e.stopPropagation();
          onPreviousTrack();
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          // Dispatch volume up event
          handleVolumeChange(0.1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          // Dispatch volume down event
          handleVolumeChange(-0.1);
          break;
        case 'KeyF':
          e.preventDefault();
          e.stopPropagation();
          // Dispatch add to favorites event
          window.dispatchEvent(new CustomEvent('addToFavoritesKeyboard'));
          break;
        case 'KeyN':
          e.preventDefault();
          e.stopPropagation();
          // Dispatch add new task event
          window.dispatchEvent(new CustomEvent('addNewTaskKeyboard'));
          break;
        case 'Slash':
          // Check for Shift key to detect '?' (Shift + /)
          if (e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();
            // Dispatch toggle keyboard shortcuts help event
            window.dispatchEvent(new CustomEvent('toggleKeyboardShortcutsHelp'));
          }
          break;
        default:
          break;
      }
    };

    // Helper to adjust volume
    const handleVolumeChange = (delta: number) => {
      const currentVolume = parseFloat(localStorage.getItem('audioVolume') || '0.7');
      const newVolume = Math.max(0, Math.min(1, currentVolume + delta));
      window.dispatchEvent(new CustomEvent('volumeChange', { detail: newVolume }));
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, onPlayPause, onNextTrack, onPreviousTrack]);
}
